import { useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';

type BlockedCustomerAlert = {
  customerId: string;
  customerName?: string;
  reason?: string;
  source: 'CHAT' | 'COMMENT' | 'CART' | 'TELEGRAM' | 'POS';
  text?: string;
};

const SOURCE_LABEL: Record<string, string> = {
  CHAT: 'by chat',
  CART: 'by adding to cart',
  COMMENT: 'by comment',
  TELEGRAM: 'via Telegram Mini App',
  POS: 'from POS storefront',
};

/**
 * App-wide listener: tells the tenant on any page when a blocked customer tries to order.
 * Includes comments even when the sale panel is closed or not mounted.
 */
export default function BlockedCustomerAlertListener() {
  const handle = useCallback((stompMsg: { body: string }) => {
    try {
      const event = JSON.parse(stompMsg.body);
      if (event.type !== 'BLOCKED_CUSTOMER_ATTEMPT') return;
      const alert = event.payload as BlockedCustomerAlert;
      toast({
        title: alert.source === 'COMMENT' ? 'Blocked customer commented' : 'Blocked customer tried to order',
        description: `${alert.customerName ?? 'Customer'} ${SOURCE_LABEL[alert.source] ?? ''}${
          alert.reason ? ` — ${alert.reason}` : ''
        }`,
        variant: 'destructive',
        duration: 8000,
      });
    } catch (e) {
      console.warn('Blocked customer WS parse error', e);
    }
  }, []);

  useWebSocketChannel('/topic/blocked-customers', handle);
  return null;
}
